import ApplicationController from "./application_controller";

/*
 * Click an element to swap a turbo-frame's content with the response
 * from a URL — without wrapping the element in an <a>. Required for
 * rich-text content that may contain nested anchors: HTML5 parsers
 * close an outer <a> early when they hit a nested one, which breaks
 * Turbo's link-click frame navigation.
 *
 * Usage
 * =====
 * <div data-controller="frame-click"
 *      data-frame-click-url-value="/path/to/edit"
 *      data-frame-click-frame-value="applicant_123_field"
 *      data-action="click->frame-click#open"
 *      data-turbo="false">
 *   ...rich text content with possibly nested <a>...
 * </div>
 *
 * Inner anchors keep working — clicks on them are not intercepted.
 *
 * Implementation note: we fetch and replace ourselves instead of
 * setting frame.src. Turbo's automatic src-change navigation is
 * unreliable on eagerly-rendered frames in this Turbo version.
 * Manual fetch+replace is deterministic.
 */
export default class extends ApplicationController {
  static values = { url: String, frame: String };

  open(event) {
    if (event.target.closest("a")) return;
    event.preventDefault();
    event.stopPropagation();

    const frame = document.getElementById(this.frameValue);
    if (!frame || frame.hasAttribute("aria-busy")) return;

    frame.setAttribute("aria-busy", "true");

    fetch(this.urlValue, {
      headers: {
        Accept: "text/html, application/xhtml+xml",
        "Turbo-Frame": this.frameValue,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const replacement = parsed.getElementById(this.frameValue);
        if (replacement) {
          frame.innerHTML = replacement.innerHTML;
        } else {
          console.warn(`[frame-click] response missing <turbo-frame id="${this.frameValue}">`);
        }
      })
      .catch((err) => {
        console.error("[frame-click] navigation failed", err);
      })
      .finally(() => {
        frame.removeAttribute("aria-busy");
      });
  }
}
